import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { APP_LANGUAGES, getLanguageOption, type AppLanguage } from '../../i18n';
import {
  warmSuccessPrimary,
  warmSuccessNeutral,
} from '../../theme/warmSuccessDesignSystem';

interface LanguageSwitcherProps {
  /** 'compact' = flag+code chip (sidebar/navbar), 'full' = dropdown with labels (settings) */
  variant?: 'compact' | 'full';
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'compact' }) => {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const currentLang = getLanguageOption(i18n.language);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (code: AppLanguage) => {
    handleClose();
    await i18n.changeLanguage(code);

    // Persist to Firestore if logged in
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await updateDoc(userDocRef, { 'preferences.language': code });
      } catch {
        // Silent fail — localStorage fallback is already saved by i18next
      }
    }
  };

  return (
    <>
      <Tooltip title={t('language.switchTo')} arrow placement="top">
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{
            borderRadius: 2,
            px: variant === 'compact' ? 1 : 1.5,
            py: 0.5,
            gap: 0.5,
            bgcolor: open ? alpha(warmSuccessPrimary[500], 0.08) : 'transparent',
            '&:hover': {
              bgcolor: alpha(warmSuccessPrimary[500], 0.06),
            },
          }}
        >
          <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>
            {currentLang.flag}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color: warmSuccessNeutral[600],
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
            }}
          >
            {currentLang.code === 'de-CH' ? 'DE' : currentLang.code.toUpperCase()}
          </Typography>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              minWidth: 180,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              border: `1px solid ${warmSuccessNeutral[200]}`,
              mt: 0.5,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {APP_LANGUAGES.map((lang) => {
          const isActive = i18n.language === lang.code;
          return (
            <MenuItem
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              selected={isActive}
              sx={{
                py: 1,
                px: 2,
                gap: 1.5,
                '&.Mui-selected': {
                  bgcolor: alpha(warmSuccessPrimary[500], 0.08),
                },
                '&:hover': {
                  bgcolor: alpha(warmSuccessPrimary[500], 0.06),
                },
              }}
            >
              <Typography sx={{ fontSize: '1.1rem', lineHeight: 1 }}>
                {lang.flag}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '0.825rem',
                    fontWeight: isActive ? 600 : 400,
                    color: warmSuccessNeutral[800],
                  }}
                >
                  {lang.nativeName}
                </Typography>
                {variant === 'full' && (
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: warmSuccessNeutral[500],
                    }}
                  >
                    {lang.label}
                  </Typography>
                )}
              </Box>
              {isActive && (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: warmSuccessPrimary[500],
                    flexShrink: 0,
                  }}
                />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
